<template>
  <router-link
    :to="linkTo"
    class="nav-link group relative z-10"
    :class="isActive ? 'nav-link-active' : 'nav-link-inactive'"
    v-slot="{ isActive: linkIsActive }"
  >
    <div class="flex items-center space-x-3 w-full">
      <!-- Icon -->
      <component
        :is="icon"
        class="w-5 h-5 flex-shrink-0 transition-colors duration-200"
        :class="linkIsActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'"
      />

      <!-- Label with transition -->
      <Transition name="fade">
        <span
          v-if="isOpen"
          class="text-sm font-medium truncate transition-colors duration-200"
          :class="linkIsActive ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'"
        >
          {{ label }}
        </span>
      </Transition>
      
      <!-- Badge -->
      <Transition name="fade">
        <span
          v-if="isOpen && badge"
          class="ml-auto badge"
          :class="badgeClasses"
        >
          {{ badge.text }}
        </span>
      </Transition>
    </div>
    
    <!-- Tooltip for collapsed state -->
    <div
      v-if="!isOpen"
      class="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap"
    >
      {{ label }}
      <div class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
    </div>
  </router-link>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  to: {
    type: Object,
    required: true
  },
  icon: {
    type: Object,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  isOpen: {
    type: Boolean,
    default: true
  },
  badge: {
    type: Object,
    default: null
  }
})

const route = useRoute()

// Compute the link with locale parameter preserved
const linkTo = computed(() => {
  const locale = route.params.locale
  if (locale && props.to.name && !props.to.params?.locale) {
    return {
      ...props.to,
      params: { ...props.to.params, locale }
    }
  }
  return props.to
})

const isActive = computed(() => {
  if (props.to.name) {
    return route.name === props.to.name || route.name?.startsWith(props.to.name + '.')
  }
  return route.path === props.to.path
})

const badgeClasses = computed(() => {
  if (!props.badge) return ''
  
  const baseClasses = 'badge'
  const variantClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    error: 'badge-error',
    info: 'badge-info',
    gray: 'badge-gray'
  }
  
  return `${baseClasses} ${variantClasses[props.badge.variant] || variantClasses.gray}`
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>